import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getPerformanceList, getClientPerformanceDetail } from '../../lib/api/clientPerformanceStat'; 
import KPISummaryCards from '../../components/clientPerformanceStat/KPISummaryCards';
import ProjectListTable from '../../components/clientPerformanceStat/ProjectListTable';
import MaintenanceContractTable from '../../components/clientPerformanceStat/MaintenanceContractTable';
import AnnualFinancialTrendChart from '../../components/clientPerformanceStat/AnnualFinancialTrendChart';
import SalesActivityTimeline from '../../components/clientPerformanceStat/SalesActivityTimeline';
import ClientSearchModal from '../../components/clientPerformanceStat/ClientSearchModal';

const ClientPerformanceStatContainer = ({ clientId }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [performanceList, setPerformanceList] = useState([]); 
    const [selectedClient, setSelectedClient] = useState({ clientCmp: '', partnerNm: '' });
    const [loading, setLoading] = useState(true);
    const [searchYear, setSearchYear] = useState(String(new Date().getFullYear()));

    // 🎯 [교정 및 보완] 상태 스토리지 이름 일치 및 초기 렌더링 트랙바 오류 방지용 trendData 초기화
    const [kpiCardsState, setKpiCardsState] = useState({
        total: { count: 0, sales: 0, buy: 0, profit: 0, margin: "0.0", ratio: "0.0%" },
        yearly: { count: 0, sales: 0, buy: 0, profit: 0, margin: "0.0", ratio: "0.0%" },
        project: { count: 0, sales: 0, buy: 0, profit: 0, margin: "0.0", ratio: "0.0%" },
        maintenance: { count: 0, sales: 0, buy: 0, profit: 0, margin: "0.0", ratio: "0.0%" },
        trendData: {
            current: { sales: 0, buy: 0, profit: 0, margin: "0.0" },
            past: { sales: 0, buy: 0, profit: 0, margin: "0.0" },
            currentYear: "",
            pastYear: ""
        }
    });

    const yearsToFetch = useMemo(() => [searchYear], [searchYear]);
    const [projectDetailList, setProjectDetailList] = useState([]);
    const [maintenanceDetailList, setMaintenanceDetailList] = useState([]);
    const [forecastProjectDetailList, setForecastProjectDetailList] = useState([]);
    const [forecastMaintenanceDetailList, setForecastMaintenanceDetailList] = useState([]);
    const [clientActionListState, setClientActionListState] = useState([]);
    const [viewMode, setViewMode] = useState('actual');

    // 1. 전사 종합 리스트 수집 파이프라인
    const loadDashboardData = useCallback(async () => {
        try {
            setLoading(true);
            const responses = await Promise.all(
                yearsToFetch.map(year => getPerformanceList(year))
            );

            let newPerformanceData = [];
            responses.forEach((res) => {
                const list = res?.data?.list || [];
                newPerformanceData = [...newPerformanceData, ...list];
            });

            const uniqueMap = new Map();
            newPerformanceData
                .filter(p => p && p.clientCmp !== null && p.clientCmp !== undefined)
                .forEach(p => {
                    uniqueMap.set(p.clientCmp, p);
                });
            
            setPerformanceList(Array.from(uniqueMap.values()));
        } catch (error) {
            console.error("실적 데이터 병렬 로드 실패:", error);
        } finally {
            setLoading(false);
        }
    }, [yearsToFetch]);

    useEffect(() => {
        loadDashboardData();
    }, [loadDashboardData]);

    // 2. 선택된 고객사 상세 KPI 및 연도 트렌드 연산 파이프라인
    useEffect(() => {
        if (!selectedClient.clientCmp || performanceList.length === 0 || !searchYear) return;

        const fetchSelectedClientKpi = async () => {
            try {
                const response = await getClientPerformanceDetail(selectedClient.clientCmp, searchYear);
                const detailList = response?.data?.detail || [];
                const rawList = response?.data?.list || [];

                const forecastDetailList = response?.data?.forecastDetail || [];
                const forecastRawList = response?.data?.forecastList || [];

                const clientActionlist = response?.data?.clientActionlist || [];
                const totalCompanySales = performanceList.reduce((sum, item) => sum + Number(item.totalSales || 0), 0);

                const calculateGridMetrics = (list, countValue = 0) => {
                    let sales = 0; let buy = 0; let profit = 0;
                    list.forEach(item => {
                        sales += Number(item.totalSales || 0);
                        buy += Number(item.totalBuys || 0);
                        profit += Number(item.totalProfit || 0);
                    });
                    
                    const margin = sales > 0 ? ((profit / sales) * 100).toFixed(1) : "0.0";
                    const ratio = totalCompanySales > 0 ? ((sales / totalCompanySales) * 100).toFixed(1) : "0.0";

                    return { count: countValue, sales, buy, profit, margin, ratio: `${ratio}%` };
                };

                // ==========================================
                // 📊 1단계. rawList 기반 건수(count) 데이터 및 테이블 리스트 추출
                // ==========================================
                const totalCount = detailList.reduce((acc, item) => {
                    const count = Number(item.prjCount || 0);
                    return acc + count;
                }, 0); 

                const currentYearRawList = rawList.filter(item => {
                    if (!item.expDay) return false;
                    return String(item.expDay).split('-')[0] === String(searchYear);
                });
                const yearlyCount = currentYearRawList.length;
                
                const projectRawList = currentYearRawList.filter(item => item.prjTyp !== 'M');
                const maintenanceRawList = currentYearRawList.filter(item => item.prjTyp === 'M');

                const projectCount = projectRawList.length;
                const maintenanceCount = maintenanceRawList.length;

                const currentYearForecastRawList = forecastRawList.filter(item => {
                    if (!item.expDay) return false;
                    return String(item.expDay).split('-')[0] === String(searchYear);
                });

                const forecastYearlyCount = currentYearForecastRawList.length;

                const forecastProjectRawList = currentYearForecastRawList.filter(item => item.prjTyp !== 'M');
                const forecastMaintenanceRawList = currentYearForecastRawList.filter(item => item.prjTyp === 'M');

                const forecastProjectCount = forecastProjectRawList.length;
                const forecastMaintenanceCount = forecastMaintenanceRawList.length;
                
                const pastYear = String(Number(searchYear) - 1);
                const pastYearCount = rawList.filter(item => String(item.expDay).split('-')[0] === pastYear).length;

                // ==========================================
                // 💰 2단계. detailList 기반 금액 필터링 및 조립 (기존 형태 유지)
                // ==========================================
                const totalMetrics = calculateGridMetrics(detailList, totalCount); 
                
                const currentYearDetailList = detailList.filter(item => {
                    if (!item.expDay) return false;
                    return String(item.expDay).split('-')[0] === String(searchYear);
                });

                const yearlyMetrics = calculateGridMetrics(currentYearDetailList, yearlyCount);
                
                const projectDetailFilterList = currentYearDetailList.filter(item => item.prjTyp !== 'M');
                const projectMetrics = calculateGridMetrics(projectDetailFilterList, projectCount);

                const maintenanceDetailList = currentYearDetailList.filter(item => item.prjTyp === 'M');
                const maintenanceMetrics = calculateGridMetrics(maintenanceDetailList, maintenanceCount);

                const currentYearForecastDetailList = forecastDetailList.filter(item => {
                    if (!item.expDay) return false;
                    return String(item.expDay).split('-')[0] === String(searchYear);
                });

                const forecastYearlyMetrics = calculateGridMetrics(currentYearForecastDetailList, forecastYearlyCount);
                const foreCastProjectDetailFilterList = currentYearForecastDetailList.filter(item => item.prjTyp !== 'M');
                const foreCastProjectMetrics = calculateGridMetrics(foreCastProjectDetailFilterList, forecastProjectCount);

                const foreCastMaintenanceDetailList = currentYearForecastDetailList.filter(item => item.prjTyp === 'M');
                const foreCastMaintenanceMetrics = calculateGridMetrics(foreCastMaintenanceDetailList, forecastMaintenanceCount);

                const pastYearDetailList = detailList.filter(item => {
                    if (!item.expDay) return false;
                    return String(item.expDay).split('-')[0] === pastYear;
                });
                const pastYearMetrics = calculateGridMetrics(pastYearDetailList, pastYearCount);


                // ==========================================
                // 🎯 3단계. KPI 카드 상태 업데이트 (기존 형태 유지)
                // ==========================================
                setKpiCardsState({
                    total: totalMetrics,
                    yearly: yearlyMetrics,
                    project: projectMetrics,
                    maintenance: maintenanceMetrics,
                    forecastProject: foreCastProjectMetrics,
                    forecastMaintenance: foreCastMaintenanceMetrics,
                    trendData: {
                        current: yearlyMetrics,
                        currentYear: searchYear,
                        past: pastYearMetrics,
                        pastYear: pastYear,
                        forecastCurrent: forecastYearlyMetrics
                    }
                });

                // ==========================================
                // 🎯 4단계. 추가 요구사항 반영 (리스트 내역 참조 세팅)
                // ==========================================
                setProjectDetailList(projectRawList);
                setMaintenanceDetailList(maintenanceRawList);
                setForecastProjectDetailList(forecastProjectRawList);
                setForecastMaintenanceDetailList(forecastMaintenanceRawList);


                // ==========================================
                // 🎯 5단계. [신규 추가] 자바 영업활동 리스트 데이터 바인딩
                // ==========================================
                setClientActionListState(clientActionlist);

            } catch (error) {
                console.error("고객사 상세 지표 영역 추출 및 트렌드 데이터 생성 실패:", error);
            }
        };

        fetchSelectedClientKpi();
    }, [selectedClient.clientCmp, searchYear, performanceList]);




        // 3. 순위 산출 계산 메모이제이션
    const clientRankingInfo = useMemo(() => {
        // 실시간 검색/필터링이 적용된 performanceList에서 현재 선택된 고객사(selectedClient) 데이터를 찾습니다.
        const currentClientData = performanceList.find(c => c.clientCmp === selectedClient.clientCmp);

        // 고객사 데이터가 없거나 리스트가 비어있다면 대시(-) 처리
        if (!currentClientData || performanceList.length === 0) {
            return { salesRank: "-", profitRank: "-" };
        }

        // 🎯 [본질 수정] 프론트엔드 정렬 로직을 전부 제거하고, 
        // DB 쿼리에서 AS 참조로 안전하게 넘어온 고정 순위 컬럼을 그대로 꺼내 매핑합니다.
        return {
            salesRank: currentClientData.salesRank || "-",
            profitRank: currentClientData.profitRank || "-"
        };
    }, [performanceList, selectedClient.clientCmp]);

    const handleSelectClient = useCallback((client) => {
        setSelectedClient({ 
            clientCmp: client.clientCmp, 
            partnerNm: client.partnerNm || client.clientCmp 
        });
    }, []);

    const handleProjectClick = (project) => {
        // 🎯 프로젝트 테이블에서 가공되기 전 원본 API 아이템 객체를 가져옵니다.
        const rawData = project?.rawItem;
        if (!rawData) return;

        // 1. 필수 값인 prjUuid 검증 (null, undefined, 공백 문자열 체크)
        const prjUuid = rawData.prjUuid;
        if (!prjUuid || String(prjUuid).trim() === '' || String(prjUuid) === 'null') {
            return;
        }

        // 2. 기본 이동 페이지 설정 (기본값: 기안/기회 관리)
        let pageUrl = "/chancemng";

        // 3. 상태값 및 등급 설정 검증
        const status = rawData.cmpStatus;
        const hasStatus = status !== null && status !== undefined && String(status).trim() !== '' && String(status) !== 'null';

        // 기본 등급 타겟 설정
        let stepTarget = (rawData.cstGrade !== null && rawData.cstGrade !== undefined && String(rawData.cstGrade) !== 'null') 
            ? rawData.cstGrade 
            : '0';
        
        // 4. 계약 상태(cmpStatus)가 존재하는 경우의 조건 분기
        if (hasStatus) {
            // 프로젝트 타입(prjTyp)이 신규('N')인 경우와 유지보수인 경우 분기
            if (String(rawData.prjTyp).trim() === 'N') {
                pageUrl = "/projectmng";
            } else if(String(rawData.prjTyp).trim() === 'M'){
                pageUrl = "/maintancetmng";
            } else {
                pageUrl = "/etcmng"
            }
            // 상태가 있으면 cmpGrade를 우선 적용
            stepTarget = (rawData.cmpGrade !== null && rawData.cmpGrade !== undefined && String(rawData.cmpGrade) !== 'null') 
                ? rawData.cmpGrade 
                : '0';
        }
        
        // 5. 새 창 띄우기 수행 또는 경고 메시지 출력
        if (stepTarget) {
            window.open(`${pageUrl}/${prjUuid}/${stepTarget}/1`, '_blank');
        }
    };

    return (
        <div style={{ padding: '0px', backgroundColor: 'transparent', display: 'flex', flexDirection: 'column', gap: '14px', boxSizing: 'border-box', fontFamily: 'sans-serif' }}>
            <div style={{ width: '100%' }}>
                <KPISummaryCards 
                    searchYear={searchYear}
                    setSearchYear={setSearchYear}
                    clientName={selectedClient.partnerNm} 
                    salesRank={clientRankingInfo.salesRank}
                    profitRank={clientRankingInfo.profitRank}
                    kpiCardsData={kpiCardsState} 
                    kpiData={kpiCardsState.yearly} 
                    onClientSearchClick={() => setIsModalOpen(true)} 
                />
            </div>

            <div style={{ display: 'flex', flexDirection: 'row', gap: '24px', width: '100%', alignItems: 'start' }}>
                {/* 왼쪽 컴포넌트 그룹 */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', flex: '2.3 1 0%', boxSizing: 'border-box', minWidth: '0' }}>
                    
                    {/* 1. 프로젝트 테이블 영역 (고정 height 제거, 필요시 최소 높이만 지정) */}
                    <div style={{ width: '100%', minHeight: '200px', transition: 'all 0.3s ease' }}>
                        <ProjectListTable searchYear={searchYear} projectDetailList={projectDetailList} forecastProjectDetailList={forecastProjectDetailList} viewMode={viewMode} setViewMode={setViewMode} onProjectClick={handleProjectClick}/>
                    </div>
                    
                    {/* 2. 유지보수 테이블 영역 (고정 height 제거) */}
                    <div style={{ width: '100%', minHeight: '200px', transition: 'all 0.3s ease' }}>
                        <MaintenanceContractTable searchYear={searchYear} maintenanceDetailList={maintenanceDetailList} forecastMaintenanceDetailList={forecastMaintenanceDetailList} viewMode={viewMode} setViewMode={setViewMode} onProjectClick={handleProjectClick}/>
                    </div>
                    
                    {/* 3. 차트 영역 (차트 컴포넌트 특성에 맞게 반응형 유지 또는 고정) */}
                    <div style={{ width: '100%', height: '180px' }}>
                        <AnnualFinancialTrendChart trendData={kpiCardsState.trendData} viewMode={viewMode} setViewMode={setViewMode} />
                    </div>
                </div>

                {/* 오른쪽 타임라인 영역 (왼쪽이 늘어나면 sticky 기능 덕분에 자연스럽게 화면을 따라 내려옵니다) */}
                <div style={{ flex: '1 1 0%', position: 'sticky', top: '14px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto', minWidth: '0' }}>
                    <SalesActivityTimeline 
                        clientActionList={clientActionListState} 
                        searchYear={searchYear}
                    />
                </div>
            </div>
            <ClientSearchModal 
                isOpen={isModalOpen} 
                onClose={() => setIsModalOpen(false)} 
                onSelectClient={handleSelectClient}
                rawClientList={performanceList} 
                searchYear={searchYear}
            />
        </div>
    );
};

export default ClientPerformanceStatContainer;
